import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Timeline, YFrame } from './types';

const PALETTE = 6;

const tc = (s: number): string => {
  const r = Math.round(s);
  return `${String(Math.floor(r / 60)).padStart(2, '0')}:${String(r % 60).padStart(2, '0')}`;
};

interface Props {
  timeline: Timeline;
  /** Signed URL of the source video. Without it the panel still follows the seek bar. */
  videoUrl: string | null;
  /** Keyframe thumbnails; scene cards show the ones inside their span. Absent or empty: no thumbnails. */
  frames?: YFrame[];
}

/**
 * Y-Ray viewer: the video, a seek bar with moments / scenes / per-person lanes, and a panel that
 * follows the playhead (scene, who is on screen, what they do, what is said). Reads only the
 * timeline; there are no bounding boxes (Tier 2 of the plan, not the frame overlay).
 */
const YRayViewer: React.FC<Props> = ({ timeline, videoUrl, frames = [] }) => {
  const dur = timeline.duration || 1;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);
  const playheadRef = useRef<HTMLDivElement | null>(null);
  const manualT = useRef(0);
  const dragging = useRef(false);
  const [time, setTime] = useState(0);
  const [videoFailed, setVideoFailed] = useState(!videoUrl);

  useEffect(() => { setVideoFailed(!videoUrl); }, [videoUrl]);

  // People: one stable colour each, labelled "Person A · host".
  const personIndex = useMemo(() => {
    const m = new Map<string, number>();
    timeline.people.forEach((p, i) => m.set(p.id, i % PALETTE));
    return m;
  }, [timeline.people]);
  const label = useCallback((id: string) => {
    const p = timeline.people.find(x => x.id === id);
    return `Person ${id}${p?.role ? ` · ${p.role}` : ''}`;
  }, [timeline.people]);
  const Chip = useCallback(({ id }: { id: string }) => (
    <span className={`yr-chip yr-c${personIndex.get(id) ?? 0}`}><i />{label(id)}</span>
  ), [personIndex, label]);

  const seek = useCallback((t: number, scroll = false) => {
    const next = Math.min(Math.max(t, 0), dur);
    manualT.current = next;
    const v = videoRef.current;
    if (v && !videoFailed) { try { v.currentTime = next; } catch { /* not loaded yet */ } }
    setTime(next);
    if (scroll && stageRef.current && window.innerWidth > 900 && stageRef.current.getBoundingClientRect().top < 0) {
      stageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [dur, videoFailed]);

  // One frame loop: playhead moves every frame (no React render), the panel updates ~10×/s.
  useEffect(() => {
    let raf = 0;
    let last = -1;
    const loop = () => {
      const v = videoRef.current;
      const t = !videoFailed && v && !v.error && !dragging.current ? v.currentTime : manualT.current;
      if (!dragging.current && !videoFailed) manualT.current = t;
      if (playheadRef.current) playheadRef.current.style.left = `${(t / dur) * 100}%`;
      if (Math.abs(t - last) >= 0.1) { last = t; setTime(t); }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [dur, videoFailed]);

  const seekFromPointer = (e: React.PointerEvent) => {
    const r = barRef.current?.getBoundingClientRect();
    if (!r || r.width === 0) return;
    seek((Math.min(Math.max(e.clientX - r.left, 0), r.width) / r.width) * dur);
  };

  // What is happening at `time`.
  const at = <T extends { start: number; end: number }>(arr: T[] | undefined): T[] =>
    (arr || []).filter(x => time >= x.start && time < x.end);
  let sceneIdx = -1;
  timeline.scenes.forEach((s, i) => { if (time >= s.start) sceneIdx = i; });
  const scene = timeline.scenes[sceneIdx];
  const second = timeline.seconds[Math.min(Math.floor(time), timeline.seconds.length - 1)];
  const shot = at(timeline.shots)[0];
  const speechIdx = timeline.speech.findIndex(s => time >= s.start && time < s.end);
  const speech = timeline.speech[speechIdx];
  const actions = at(timeline.actions);
  const ocr = at(timeline.ocr);
  const audio = at(timeline.audio);
  const onScreen = second?.people || [];

  const moderation = timeline.derived?.moderation;
  const skipped = (timeline.meta?.stages || []).filter(s => s.status !== 'ok');

  return (
    <div className="yr-viewer">
      <div className="yr-stage" ref={stageRef}>
        <div className="yr-left">
          <div className="yr-video-wrap">
            {videoUrl && (
              <video
                ref={videoRef}
                className="yr-video"
                src={videoUrl}
                controls
                playsInline
                preload="metadata"
                onError={() => setVideoFailed(true)}
              />
            )}
            {videoFailed && (
              <div className="yr-video-missing">
                Video unavailable. The panel still follows the seek bar below.
              </div>
            )}
          </div>

          <div
            className="yr-bar"
            ref={barRef}
            role="slider"
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={Math.round(dur)}
            aria-valuenow={Math.round(time)}
            onPointerDown={e => { dragging.current = true; e.currentTarget.setPointerCapture(e.pointerId); seekFromPointer(e); }}
            onPointerMove={e => { if (dragging.current) seekFromPointer(e); }}
            onPointerUp={() => { dragging.current = false; }}
            onPointerCancel={() => { dragging.current = false; }}
          >
            <div className="yr-lane yr-lane-mo">
              {timeline.moments.map((m, i) => (
                <i
                  key={i}
                  className={`yr-seg yr-mo-${m.kind === 'hook' || m.kind === 'peak' ? m.kind : 'other'}`}
                  style={{ left: `${(m.start / dur) * 100}%`, width: `${((m.end - m.start) / dur) * 100}%` }}
                  title={`${m.kind} ${tc(m.start)}–${tc(m.end)}${m.reason ? ` · ${m.reason}` : ''}`}
                >{m.kind}</i>
              ))}
            </div>
            <div className="yr-lane yr-lane-sc">
              {timeline.scenes.map((s, i) => (
                <i
                  key={i}
                  className={`yr-seg${i === sceneIdx ? ' yr-seg-on' : ''}`}
                  style={{ left: `${(s.start / dur) * 100}%`, width: `${((s.end - s.start) / dur) * 100}%` }}
                  title={`Scene ${i + 1}: ${s.summary || ''}`}
                >{i + 1}</i>
              ))}
            </div>
            {timeline.people.map(p => (
              <div className="yr-lane yr-lane-pl" key={p.id}>
                {(p.tracks || []).map((k, i) => (
                  <i
                    key={i}
                    className={`yr-seg yr-c${personIndex.get(p.id) ?? 0}`}
                    style={{ left: `${(k.start / dur) * 100}%`, width: `${((k.end - k.start) / dur) * 100}%` }}
                    title={`${label(p.id)} ${tc(k.start)}–${tc(k.end)}`}
                  />
                ))}
              </div>
            ))}
            <div className="yr-playhead" ref={playheadRef} />
          </div>
          <div className="yr-legend">
            <span>Rows: moments, scenes, then who is on screen</span>
            {timeline.people.map(p => <Chip key={p.id} id={p.id} />)}
          </div>
        </div>

        <aside className="yr-now" aria-live="off">
          <span className="yr-time">{tc(time)}</span>
          <h3>Scene</h3>
          <div className="yr-scene">{scene?.summary || '—'}</div>
          <div>
            {[scene?.setting, scene?.mood, shot?.type, shot?.cameraMotion].filter(Boolean).map((x, i) => (
              <span className="yr-tag" key={i}>{x}</span>
            ))}
            {second?.broll && <span className="yr-tag">B-roll</span>}
            {second?.split && <span className="yr-tag">split screen</span>}
          </div>
          <h3>On screen</h3>
          <div>
            {onScreen.length ? onScreen.map(id => <Chip key={id} id={id} />) : <span className="yr-mut">no one on screen</span>}
          </div>
          {actions.map((a, i) => (
            <div className="yr-act" key={`${a.personId}${i}`}>
              <Chip id={a.personId} />{a.action}
              {a.emotion && a.emotion !== 'neutral' && <span className="yr-mut"> ({a.emotion})</span>}
            </div>
          ))}
          <h3>Saying</h3>
          <div>
            {speech ? <>{speech.speaker && <Chip id={speech.speaker} />} {speech.text}</> : <span className="yr-mut">no speech</span>}
          </div>
          {ocr.length > 0 && (<><h3>On-screen text</h3>{ocr.map((o, i) => <div key={i}>{o.text}</div>)}</>)}
          {audio.length > 0 && (<><h3>Audio</h3>{audio.map((a, i) => <span className="yr-tag" key={i}>{a.type}</span>)}</>)}
        </aside>
      </div>

      {skipped.length > 0 && (
        <div className="yr-card yr-partial">
          <b>Partial result.</b> Skipped: {skipped.map(s => `${s.name}${s.note ? ` (${s.note})` : ''}`).join(', ')}.
        </div>
      )}

      <h2 className="yr-h2">Summary</h2>
      <div className="yr-card">
        <div>{timeline.derived?.summary || 'No summary.'}</div>
        <div>{(timeline.derived?.topics || []).map(x => <span className="yr-tag" key={x}>{x}</span>)}</div>
        {moderation && (
          <div className="yr-mut">
            moderation: {typeof moderation === 'string' ? moderation : `${moderation.level || 'unknown'}${moderation.flags?.length ? ` (${moderation.flags.join(', ')})` : ''}`}
          </div>
        )}
      </div>

      <h2 className="yr-h2">People</h2>
      {timeline.people.map(p => (
        <div className="yr-card" key={p.id}>
          <Chip id={p.id} />
          <span className="yr-mut">
            {[p.look, p.ageRange, p.screenTime != null ? `on screen ${Math.round(p.screenTime)}s` : '', p.talkTime != null ? `talks ${Math.round(p.talkTime)}s` : ''].filter(Boolean).join(' · ')}
          </span>
        </div>
      ))}

      <h2 className="yr-h2">Key moments</h2>
      {timeline.moments.map((m, i) => (
        <button type="button" className="yr-card yr-row" key={i} onClick={() => seek(m.start, true)}>
          <span className="yr-t">{tc(m.start)}–{tc(m.end)}</span> <span className="yr-tag">{m.kind}</span> score {m.score}
          {m.reason && <span className="yr-mut"> {m.reason}</span>}
        </button>
      ))}

      <h2 className="yr-h2">Scenes</h2>
      {timeline.scenes.map((s, i) => (
        <button type="button" className="yr-card yr-row" key={i} onClick={() => seek(s.start, true)}>
          <span className="yr-t">{tc(s.start)}–{tc(s.end)}</span> <span className="yr-mut">scene {i + 1}</span>
          <div>{s.summary}</div>
          {s.setting && <div className="yr-mut">{s.setting}</div>}
          <div>
            {s.mood && <span className="yr-tag">{s.mood}</span>}
            {(s.objects || []).map(o => <span className="yr-tag" key={o}>{o}</span>)}
          </div>
          {frames.some(f => f.time >= s.start && f.time < s.end) && (
            <div className="yr-thumbs">
              {frames.filter(f => f.time >= s.start && f.time < s.end).map(f => (
                <img
                  key={f.time}
                  src={f.url}
                  alt={`Frame at ${tc(f.time)}`}
                  title={tc(f.time)}
                  loading="lazy"
                  onClick={e => { e.stopPropagation(); seek(f.time, true); }}
                />
              ))}
            </div>
          )}
        </button>
      ))}

      <h2 className="yr-h2">Speech</h2>
      <div className="yr-card">
        <details>
          <summary>{timeline.speech.length} sentences</summary>
          <div className="yr-speech">
            {timeline.speech.map((s, i) => (
              <React.Fragment key={i}>
                <button type="button" className={`yr-t yr-link${i === speechIdx ? ' yr-on' : ''}`} onClick={() => seek(s.start, true)}>
                  {tc(s.start)}{s.speaker ? ` ${s.speaker}` : ''}
                </button>
                <span className={i === speechIdx ? 'yr-on' : undefined}>{s.text}</span>
              </React.Fragment>
            ))}
          </div>
        </details>
      </div>

      <h2 className="yr-h2">Per-second detections</h2>
      <div className="yr-card">
        <details>
          <summary>{timeline.seconds.length} seconds</summary>
          <div className="yr-table-wrap">
            <table className="yr-table">
              <thead><tr><th>Sec</th><th>Time</th><th>People on screen</th><th>B-roll</th><th>Split</th></tr></thead>
              <tbody>
                {timeline.seconds.map((r, i) => (
                  <tr key={i} className={i === Math.floor(time) ? 'yr-on' : undefined}>
                    <td>{i + 1}</td>
                    <td><button type="button" className="yr-t yr-link" onClick={() => seek(r.start, true)}>{tc(r.start)}–{tc(r.end)}</button></td>
                    <td>{(r.people || []).map(id => `Person ${id}`).join(', ') || '—'}</td>
                    <td>{r.broll ? 'Yes' : 'No'}</td>
                    <td>{r.split ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      </div>

      {(timeline.meta?.stages || []).length > 0 && (
        <>
          <h2 className="yr-h2">Stages</h2>
          <div className="yr-card">
            <div className="yr-table-wrap">
              <table className="yr-table">
                <thead><tr><th>Stage</th><th>Status</th><th>Time</th><th>Note</th></tr></thead>
                <tbody>
                  {(timeline.meta?.stages || []).map(st => (
                    <tr key={st.name}>
                      <td>{st.name}</td>
                      <td>{st.status}</td>
                      <td>{st.ms != null ? `${(st.ms / 1000).toFixed(1)}s` : '—'}</td>
                      <td className="yr-mut">{st.note || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default YRayViewer;
